import React, { useState, useEffect } from 'react';
import './App.css';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { ActivityCard } from './components/ActivityCard';
import { ActivityModal } from './components/ActivityModal';
import { ComeFunziona } from './components/ComeFunziona';
import { Regolamento } from './components/Regolamento';
import { VeCoPoint } from './components/VeCoPoint';
import { Footer } from './components/Footer';
import { AdminLogin } from './components/admin/AdminLogin';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { RequestPasswordReset } from './components/admin/RequestPasswordReset';
import { ResetPassword } from './components/admin/ResetPassword';
import { CategoryManager } from './components/admin/CategoryManager'; 
import { Tabs, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import { getActivities, getCategories, isAdminAuthenticated } from './services/api'; 
import { toast } from 'sonner'; 

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuth = isAdminAuthenticated();
  return isAuth ? children : <Navigate to="/admin" />;
};

// Home Component
const Home = () => {
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [categories, setCategories] = useState(['Tutte le categorie']); 
  
  const [searchFilters, setSearchFilters] = useState({
    searchTerm: '',
    zone: 'Tutte le città',
    category: 'Tutte le categorie'
  });
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load Initial Data (Activities + Categories)
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [activitiesData, categoriesData] = await Promise.all([
          getActivities(),
          getCategories()
        ]);

        setActivities(activitiesData);
        setFilteredActivities(activitiesData);
        
        const categoryNames = categoriesData.map(c => c.name);
        setCategories(['Tutte le categorie', ...categoryNames]);

      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Errore nel caricamento dei dati.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filter activities based on search criteria
  const handleSearch = async () => {
    try {
      const data = await getActivities(searchFilters);
      setFilteredActivities(data);
      
      const element = document.getElementById('activities');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error searching activities:', error);
    }
  };

  // Auto-filter when category changes
  useEffect(() => {
    if (!loading) {
        handleSearch();
    }
    // Rimosso il commento eslint che causava l'errore di build
  }, [searchFilters.category]); 

  const handleActivityClick = (activity) => {
    setSelectedActivity(activity);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <HeroSection
        onSearch={handleSearch}
        searchFilters={searchFilters}
        setSearchFilters={setSearchFilters}
      />

      {/* Activities Section */}
      <section id="activities" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Attività convenzionate
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
                Esplora le attività partner che offrono sconti esclusivi ai tesserati VeCo Shop
              </p>
              
              {/* Category Tabs DINAMICI */}
              <Tabs
                value={searchFilters.category}
                onValueChange={(value) => setSearchFilters({ ...searchFilters, category: value })}
                className="w-full"
              >
                <TabsList className="inline-flex flex-wrap h-auto gap-2 bg-gray-100 p-2 rounded-lg justify-center">
                  {categories.map((category) => (
                    <TabsTrigger
                      key={category}
                      value={category}
                      className="data-[state=active]:bg-teal-600 data-[state=active]:text-white transition-all"
                    >
                      {category}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>

            {/* Results Count */}
            <div className="mb-6 flex justify-center">
              <Badge variant="outline" className="text-base px-4 py-2">
                {filteredActivities.length} {filteredActivities.length === 1 ? 'attività trovata' : 'attività trovate'}
              </Badge>
            </div>

            {/* Activities Grid */}
            {loading ? (
              <div className="text-center py-20">
                <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Caricamento in corso...</p>
              </div>
            ) : filteredActivities.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredActivities.map((activity) => (
                  <ActivityCard
                    key={activity.id}
                    activity={activity}
                    onClick={handleActivityClick}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl">🔍</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Nessuna attività trovata</h3>
                <p className="text-gray-600 mb-6">Prova a modificare i filtri di ricerca</p>
                <button
                  onClick={() => {
                    setSearchFilters({
                      searchTerm: '',
                      zone: 'Tutte le città',
                      category: 'Tutte le categorie'
                    });
                    getActivities().then(data => setFilteredActivities(data));
                  }}
                  className="text-teal-600 hover:text-teal-700 font-semibold underline"
                >
                  Ripristina filtri
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <ComeFunziona />
      <Regolamento />
      <VeCoPoint />
      <Footer />

      {/* Activity Detail Modal */}
      <ActivityModal
        activity={selectedActivity}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

// Main App Component
function App() {
  return (
    <>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/request-reset" element={<RequestPasswordReset />} />
        <Route path="/admin/reset-password" element={<ResetPassword />} />
        
        {/* Rotte Protette Admin */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/admin/categories" 
          element={
            <ProtectedRoute>
              <CategoryManager />
            </ProtectedRoute>
          } 
        />
      </Routes>
      <Toaster position="top-right" />
    </>
  );
}

export default App;